import { useEffect, useState } from "react";

// Module-level, component-independent tracking of the live-call start time.
// Registered ONCE at import time so the start is captured even when no timer
// component is mounted yet (e.g. voice-only mode mounts the control bar only
// AFTER the async screen-pop completes, well after `dialer:bridged` fired).
if (typeof window !== "undefined" && !window.__c7CallTimerBound) {
  window.__c7CallTimerBound = true;
  window.__c7CallStart = window.__c7CallStart || null;
  window.addEventListener("dialer:bridged", () => { window.__c7CallStart = Date.now(); });
  window.addEventListener("dialer:ended", () => { window.__c7CallStart = null; });
}

// Shared live-call talk-timer. Reads the start time maintained above, so it
// works whether the consuming component mounts before OR after the call is
// bridged. Freezes (stops incrementing, keeps last value) when `frozen` is
// true (e.g. customer dropped / call ended). Used by the LiveCallControlBar
// and the ConversationView live-call badge so talk-time stays in sync across
// the voice-only and omnichannel surfaces.
export function useCallTimer(frozen = false) {
  const [startTime, setStartTime] = useState(() => (typeof window !== "undefined" ? window.__c7CallStart || null : null));
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const onBridge = () => { setStartTime(window.__c7CallStart || Date.now()); setElapsed(0); };
    const onEnd = () => { setStartTime(null); setElapsed(0); };
    window.addEventListener("dialer:bridged", onBridge);
    window.addEventListener("dialer:ended", onEnd);
    // Sync with any start that fired BEFORE this component mounted.
    if (window.__c7CallStart && startTime == null) setStartTime(window.__c7CallStart);
    return () => {
      window.removeEventListener("dialer:bridged", onBridge);
      window.removeEventListener("dialer:ended", onEnd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (startTime == null) return;
    const tick = () => setElapsed(Math.floor((Date.now() - startTime) / 1000));
    tick();
    if (frozen) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTime, frozen]);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  return { elapsed, mmss: `${mm}:${ss}`, active: startTime != null };
}

export default useCallTimer;
